import { useState } from 'react';
import { useRepo } from '../useStore.js';
import { SIDEBAR, clampSidebar } from './usePrefs.js';
import { useResizer } from './useResizer.js';
import { fmt, wc } from './util.js';

const TOOLS = [
  ['board', 'Corkboard'],
  ['notes', 'Characters & World'],
  ['goals', 'Tools & Settings']
];

export default function Sidebar({ nav, go, space, newDoc, width, onWidth }) {
  const repo = useRepo();
  const [shown, grip] = useResizer({
    width, onWidth, limits: SIDEBAR, clamp: clampSidebar,
    edge: 'right', className: 'side-grip', label: 'Resize the document list'
  });
  const isJournal = space?.id === 'journal';
  const docs = space ? space.docs : [];

  // `gap` is where the dragged document would land: before docs[gap], or at the
  // end when it equals docs.length.
  const [dragId, setDragId] = useState(null);
  const [gap, setGap] = useState(null);
  const from = docs.findIndex(d => d.id === dragId);
  // Dropping a document either side of itself changes nothing, so show nothing.
  const showGap = gap !== null && gap !== from && gap !== from + 1;

  const endDrag = () => { setDragId(null); setGap(null); };

  const onDragOver = (e, i) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const box = e.currentTarget.getBoundingClientRect();
    setGap(e.clientY < box.top + box.height / 2 ? i : i + 1);
  };

  const onDrop = (e) => {
    e.preventDefault();
    // moveDocument counts positions among the *other* documents.
    if (showGap) repo.moveDocument(dragId, gap > from ? gap - 1 : gap);
    endDrag();
  };

  // Option+↑/↓ does the same without a mouse, and keeps the focus on the document.
  const onKeyDown = (e, i, id) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    const to = e.key === 'ArrowUp' ? i - 1 : i + 1;
    if (to < 0 || to >= docs.length) return;
    repo.moveDocument(id, to);
    requestAnimationFrame(() => document.querySelector(`[data-doc-id="${id}"]`)?.focus());
  };

  // The grip sits outside the scrolling list so it stays put when the list scrolls.
  return (
    <div className="side-column" style={{ width: shown + 'px' }}>
      <nav className="side">
        <div className="side-label">{isJournal ? 'Entries' : 'Documents'}</div>
        <div
          className="side-docs"
          onDragOver={e => { if (dragId) e.preventDefault(); }}
          onDrop={onDrop}
        >
          {docs.map((d, i) => (
            <button
              key={d.id}
              data-doc-id={d.id}
              draggable
              className={'side-item'
                + (d.id === nav.docId && nav.tool === 'write' ? ' is-active' : '')
                + (d.id === dragId ? ' is-dragging' : '')
                + (showGap && gap === i ? ' drop-above' : '')
                + (showGap && gap === docs.length && i === docs.length - 1 ? ' drop-below' : '')}
              onClick={() => go({ docId: d.id, tool: 'write', mode: 'write' })}
              onDragStart={e => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', d.title || 'Untitled');
                setDragId(d.id);
              }}
              onDragOver={e => onDragOver(e, i)}
              onDragEnd={endDrag}
              onKeyDown={e => onKeyDown(e, i, d.id)}
              aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
            >
              <span className="doc-name">{d.title || 'Untitled'}</span>
              <span className="doc-words">{fmt(wc(d.content))}</span>
            </button>
          ))}
        </div>
        <button className="dashed" onClick={newDoc}>+ {isJournal ? 'New entry' : 'New document'}</button>

        <div className="side-label tools">Tools</div>
        {TOOLS.map(([id, label]) => (
          <button
            key={id}
            className={'side-tool' + (nav.tool === id ? ' is-active' : '')}
            onClick={() => go({ tool: id })}
          >{label}</button>
        ))}
      </nav>
      {grip}
    </div>
  );
}
